/* Wolf header (sections/wolf-header.liquid)
   - <wolf-header> : sticky behaviour (always / on-scroll-up), rAF-throttled.
     Reuses Dawn's section classes (shopify-section-header-sticky/-hidden,
     animate, scrolled-past-header) and --header-height so base.css handles
     the actual pinning + slide animation. Honours the 'preventHeaderReveal'
     event other theme scripts dispatch.
   - <wolf-header-drawer> : mobile slide-in menu with focus trap-lite,
     body scroll lock, Esc/backdrop close. */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 990px)';

  if (!customElements.get('wolf-header')) {
    class WolfHeader extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section') || this.parentElement;
        this.stickyType = this.getAttribute('data-sticky-type') || 'none';
        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.ticking = false;

        this.setHeaderHeight();
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.setHeaderHeight.bind(this));
          this.resizeObserver.observe(this);
        }

        if (this.stickyType === 'none') return;

        if (this.stickyType === 'always') {
          this.section.classList.add('shopify-section-header-sticky');
          return;
        }

        // on-scroll-up
        var rect = this.getBoundingClientRect();
        this.headerTop = rect.top + window.scrollY;
        this.headerBottom = rect.bottom + window.scrollY;

        this.onScrollHandler = this.onScroll.bind(this);
        this.onPreventReveal = function () {
          this.preventReveal = true;
        }.bind(this);

        window.addEventListener('scroll', this.onScrollHandler, false);
        this.addEventListener('preventHeaderReveal', this.onPreventReveal);
      }

      disconnectedCallback() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        if (this.onScrollHandler) {
          window.removeEventListener('scroll', this.onScrollHandler);
        }
        if (this.onPreventReveal) {
          this.removeEventListener('preventHeaderReveal', this.onPreventReveal);
        }
        window.clearTimeout(this.revealTimeout);
      }

      setHeaderHeight() {
        document.documentElement.style.setProperty('--header-height', this.section.offsetHeight + 'px');
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(
          function () {
            this.ticking = false;
            this.update(window.pageYOffset || document.documentElement.scrollTop);
          }.bind(this)
        );
      }

      update(scrollTop) {
        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBottom) {
          this.section.classList.add('scrolled-past-header');
          if (!this.preventHide) this.hide();
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBottom) {
          this.section.classList.add('scrolled-past-header');
          if (!this.preventReveal) {
            this.reveal();
          } else {
            window.clearTimeout(this.revealTimeout);
            this.revealTimeout = setTimeout(
              function () {
                this.preventReveal = false;
              }.bind(this),
              66
            );
            this.hide();
          }
        } else if (scrollTop <= this.headerTop) {
          this.section.classList.remove('scrolled-past-header');
          this.reset();
        }

        this.currentScrollTop = scrollTop;
      }

      hide() {
        this.section.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        this.closeDisclosures();
      }

      reveal() {
        this.section.classList.add('shopify-section-header-sticky', 'animate');
        this.section.classList.remove('shopify-section-header-hidden');
      }

      reset() {
        this.section.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      }

      closeDisclosures() {
        this.querySelectorAll('details-disclosure').forEach(function (disclosure) {
          if (typeof disclosure.close === 'function') disclosure.close();
        });
        var searchModal = this.querySelector('details-modal');
        if (searchModal && typeof searchModal.close === 'function') searchModal.close(false);
      }
    }

    customElements.define('wolf-header', WolfHeader);
  }

  if (!customElements.get('wolf-header-drawer')) {
    class WolfHeaderDrawer extends HTMLElement {
      connectedCallback() {
        this.toggleButton = this.querySelector('[data-drawer-toggle]');
        this.panel = this.querySelector('[data-drawer-panel]');
        this.backdrop = this.querySelector('[data-drawer-backdrop]');
        if (!this.toggleButton || !this.panel) return;

        this.onToggleClick = this.toggle.bind(this);
        this.onBackdropClick = this.close.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onDesktopChange = function (event) {
          if (event.matches) this.close();
        }.bind(this);

        this.toggleButton.addEventListener('click', this.onToggleClick);
        if (this.backdrop) this.backdrop.addEventListener('click', this.onBackdropClick);
        this.addEventListener('keydown', this.onKeydown);

        this.closeButton = this.querySelector('[data-drawer-close]');
        if (this.closeButton) this.closeButton.addEventListener('click', this.onBackdropClick);

        this.desktopMedia = window.matchMedia(DESKTOP_QUERY);
        this.desktopMedia.addEventListener('change', this.onDesktopChange);
      }

      disconnectedCallback() {
        if (this.toggleButton) this.toggleButton.removeEventListener('click', this.onToggleClick);
        if (this.backdrop) this.backdrop.removeEventListener('click', this.onBackdropClick);
        if (this.closeButton) this.closeButton.removeEventListener('click', this.onBackdropClick);
        this.removeEventListener('keydown', this.onKeydown);
        if (this.desktopMedia) this.desktopMedia.removeEventListener('change', this.onDesktopChange);
        document.body.classList.remove('wolf-drawer-open');
      }

      get isOpen() {
        return this.hasAttribute('open');
      }

      toggle() {
        this.isOpen ? this.close() : this.open();
      }

      open() {
        if (this.isOpen) return;
        this.setAttribute('open', '');
        this.toggleButton.setAttribute('aria-expanded', 'true');
        document.body.classList.add('wolf-drawer-open');
        this.previousFocus = document.activeElement;

        var target = this.firstFocusable() || this.panel;
        window.setTimeout(function () {
          target.focus();
        }, 100);
      }

      close() {
        if (!this.isOpen) return;
        this.removeAttribute('open');
        this.toggleButton.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('wolf-drawer-open');

        var focusTarget =
          this.previousFocus && document.contains(this.previousFocus) ? this.previousFocus : this.toggleButton;
        focusTarget.focus();
        this.previousFocus = null;
      }

      focusables() {
        return Array.prototype.filter.call(
          this.panel.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'),
          function (element) {
            return element.offsetParent !== null;
          }
        );
      }

      firstFocusable() {
        return this.focusables()[0] || null;
      }

      handleKeydown(event) {
        if (!this.isOpen) return;

        if (event.code === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;

        var focusables = this.focusables();
        if (focusables.length === 0) return;

        var first = focusables[0];
        var last = focusables[focusables.length - 1];

        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    customElements.define('wolf-header-drawer', WolfHeaderDrawer);
  }
})();
